import { useMemo } from 'react';
import type { SocialBriefItemType } from '@inference.engine/widgets/web';
import { CopyIcon, XIcon } from 'lucide-react';
import { getWidgetStores, type WidgetFullModeProps } from '@/entities/widget';
import { SidePanel, type WidgetPanelProps } from '@/shared/ui/SidePanelWrapper';
import { useBriefActions } from '../model/hooks';
import type { SocialBriefWidgetProps } from '../model/social-brief-widget.types';
import { SocialBriefDetail } from './components/SocialBriefDetail/SocialBriefDetail';
import { useTranslation } from 'react-i18next';

export const SocialBriefWidget = (props: WidgetFullModeProps<SocialBriefWidgetProps>) => {
  const { t } = useTranslation('contentTest');

  const { data } = props;
  const briefItem = data?.items[0];
  const { handleCopyMarkdown } = useBriefActions(briefItem || ({} as SocialBriefItemType));

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store/ui` module; tests stub
  // `getWidgetStores()` at the seam.
  const { uiStore } = getWidgetStores();
  const { closeSidePanel } = uiStore.useSidePanel();

  const socialBriefDetailView = useMemo(() => {
    if (!briefItem) return null;
    return (
      <div>
        <SocialBriefDetail data-testid="social-brief-detail" item={briefItem} />
      </div>
    );
  }, [briefItem]);

  const headerConfig = useMemo<WidgetPanelProps['headerConfig']>(
    () => ({
      chip: { label: t('socialBriefWidget.socialMediaBrief'), color: 'blue' },
      actions: [
        {
          type: 'icon',
          icon: <CopyIcon />,
          onClick: handleCopyMarkdown,
          tooltip: t('socialBriefWidget.copyContent'),
          ariaLabel: t('socialBriefWidget.copyContent'),
        },
        {
          type: 'icon',
          icon: <XIcon />,
          onClick: closeSidePanel,
          tooltip: t('socialBriefWidget.closeWidget'),
          ariaLabel: t('socialBriefWidget.closeWidget'),
        },
      ],
    }),
    [handleCopyMarkdown, closeSidePanel, t],
  );
  if (!data) return <div>{t('socialBriefWidget.noDataProvidedForSocialBriefWidget')}</div>;
  if (data.items.length !== 1)
    return <div>{t('socialBriefWidget.invalidDataProvidedForSocialBriefWidget')}</div>;

  return <SidePanel headerConfig={headerConfig}>{socialBriefDetailView}</SidePanel>;
};
